import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, CalendarDays, Clock, User, Eye, MessageCircle, Send, Loader2 } from "lucide-react";
import Navbar from "@/components/site/Navbar";
import { Seo } from "@/components/site/Seo";
import Footer from "@/components/site/Footer";
import FinalCTA from "@/components/site/FinalCTA";
import { RelatedPosts } from "@/components/site/RelatedPosts";
import { getPost } from "@/data/blogPosts";
import { registerView, fetchComments, postComment, fetchBlogMeta } from "@/data/blogApi";
import { useToast } from "@/hooks/use-toast";

const fmtDate = (d) =>
  new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

const Block = ({ block }) => {
  switch (block.type) {
    case "lead":
      return <p className="text-xl md:text-2xl font-heading font-semibold text-[#0A2540] leading-snug my-8">{block.text}</p>;
    case "h2":
      return <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-[#0F172A] mt-12 mb-4 leading-tight">{block.text}</h2>;
    case "ul":
      return (
        <ul className="my-6 space-y-3">
          {block.items.map((it) => (
            <li key={it} className="flex items-start gap-3 text-lg text-[#334155] leading-relaxed">
              <span className="mt-2.5 w-2 h-2 rounded-full bg-[#0EA5E9] shrink-0" /> {it}
            </li>
          ))}
        </ul>
      );
    case "pathway":
      return (
        <div className="my-8 space-y-4">
          {block.items.map((it) => (
            <div key={it.label} className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
              <h3 className="font-heading text-lg font-bold text-[#0F172A]">{it.label}</h3>
              <p className="mt-1.5 text-[#64748B] leading-relaxed">{it.text}</p>
            </div>
          ))}
        </div>
      );
    case "points":
      return (
        <div className="my-8 grid sm:grid-cols-2 gap-5">
          {block.items.map((it) => (
            <div key={it.title} className="bg-[#0A2540] rounded-2xl p-6 text-white">
              <h3 className="font-heading text-lg font-bold text-white">{it.title}</h3>
              <p className="mt-2 text-[#CBD5E1] leading-relaxed text-[15px]">{it.text}</p>
            </div>
          ))}
        </div>
      );
    case "image":
      return (
        <figure className="my-10">
          <div className="rounded-[2rem] overflow-hidden shadow-xl shadow-[#0A2540]/10 border border-slate-100 bg-white">
            <img src={block.src} alt={block.alt} loading="lazy" className="w-full h-auto object-contain" />
          </div>
          {block.caption && (
            <figcaption className="mt-3 text-center text-sm text-[#64748B]">{block.caption}</figcaption>
          )}
        </figure>
      );
    default:
      return <p className="my-5 text-lg text-[#334155] leading-relaxed">{block.text}</p>;
  }
};

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPost(slug);
  const { toast } = useToast();
  const [meta, setMeta] = useState({ views: 0, comments: 0 });
  const [comments, setComments] = useState([]);
  const [form, setForm] = useState({ name: "", text: "" });
  const [submitting, setSubmitting] = useState(false);
  const viewed = useRef(null); // slug whose view has been registered this mount

  useEffect(() => {
    if (!slug || viewed.current === slug) return;
    viewed.current = slug;
    setComments([]);
    setMeta({ views: 0, comments: 0 });
    setForm({ name: "", text: "" });
    const key = `blog-viewed-${slug}`;
    const alreadyViewed = sessionStorage.getItem(key);
    (async () => {
      let views;
      if (!alreadyViewed) {
        const m = await registerView(slug);
        sessionStorage.setItem(key, "1");
        if (m) views = m.views;
      }
      if (views === undefined) {
        const all = await fetchBlogMeta();
        views = all[slug]?.views ?? 0;
      }
      const list = await fetchComments(slug);
      if (viewed.current !== slug) return; // user has moved on to another post
      setComments(list);
      setMeta({ views, comments: list.length });
    })();
  }, [slug]);

  if (!post) return <Navigate to="/blog" replace />;

  const cta = post.cta || { label: "Explore our Sharks pathway", to: "/classes/childrens-swimming" };

  const submitComment = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.text.trim()) return;
    setSubmitting(true);
    try {
      await postComment(slug, form.name.trim(), form.text.trim());
      setForm({ name: "", text: "" });
      toast({ title: "Thanks for your comment! 🧡", description: "It'll appear here once our team has approved it." });
    } catch {
      toast({ title: "Couldn't post your comment", description: "Please try again in a moment.", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  const schema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.excerpt,
    image: post.heroImage,
    datePublished: post.date,
    author: { "@type": "Organization", name: "Happy Tiddlers Swim School" },
    publisher: { "@type": "Organization", name: "Happy Tiddlers Swim School" },
    mainEntityOfPage: `https://happytiddlers.co.uk/blog/${post.slug}`,
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[#334155] font-body" data-testid="blog-post-page">
      <Seo title={`${post.title} | Happy Tiddlers`} description={post.excerpt} path={`/blog/${post.slug}`} schema={schema} />
      <Navbar />
      <main>
        <article>
          <section className="pt-12 md:pt-16 pb-8">
            <div className="container mx-auto px-6 md:px-12 max-w-3xl">
              <Link to="/blog" className="inline-flex items-center gap-2 text-sm font-bold text-[#64748B] hover:text-[#0EA5E9] transition-colors" data-testid="blog-back-link">
                <ArrowLeft size={16} /> All articles
              </Link>
              <span className="mt-6 inline-block text-xs font-bold uppercase tracking-widest bg-[#0EA5E9]/10 text-[#0EA5E9] px-3 py-1.5 rounded-full">{post.category}</span>
              <h1 className="mt-4 font-heading text-3xl md:text-4xl lg:text-5xl font-black tracking-tight text-[#0F172A] leading-[1.08]">{post.title}</h1>
              <div className="mt-6 flex flex-wrap items-center gap-5 text-sm text-[#64748B]" data-testid="blog-post-stats">
                <span className="inline-flex items-center gap-1.5"><User size={15} /> {post.author}</span>
                <span className="inline-flex items-center gap-1.5"><CalendarDays size={15} /> {fmtDate(post.date)}</span>
                <span className="inline-flex items-center gap-1.5"><Clock size={15} /> {post.readTime}</span>
                <span className="inline-flex items-center gap-1.5" data-testid="blog-post-views"><Eye size={15} /> {meta.views} views</span>
                <span className="inline-flex items-center gap-1.5" data-testid="blog-post-comment-count"><MessageCircle size={15} /> {meta.comments} comments</span>
              </div>
            </div>
          </section>

          <div className="container mx-auto px-6 md:px-12 max-w-4xl">
            <motion.div
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              className="rounded-[2rem] overflow-hidden shadow-2xl shadow-[#0A2540]/20"
            >
              <img src={post.heroImage} alt={post.heroAlt} className="w-full h-64 md:h-[440px] object-cover" />
            </motion.div>
          </div>

          <section className="py-12 md:py-16">
            <div className="container mx-auto px-6 md:px-12 max-w-3xl">
              {post.body.map((block, i) => (
                <Block key={`${block.type}-${i}`} block={block} />
              ))}

              <div className="mt-12 pt-8 border-t border-slate-200 flex flex-wrap items-center justify-between gap-4">
                <Link to="/blog" className="inline-flex items-center gap-2 font-bold text-[#64748B] hover:text-[#0EA5E9] transition-colors">
                  <ArrowLeft size={16} /> Back to the blog
                </Link>
                <div className="flex flex-wrap items-center gap-3">
                  <Link to="/timetable" className="inline-flex items-center gap-2 bg-white text-[#0A2540] border-2 border-[#0A2540] font-bold px-6 py-3 rounded-full hover:bg-[#0A2540] hover:text-white transition-all duration-300" data-testid="blog-cta-timetable">
                    <CalendarDays size={18} /> See our timetable
                  </Link>
                  <Link to={cta.to} className="inline-flex items-center gap-2 bg-[#FF7A1A] text-white font-bold px-6 py-3 rounded-full hover:-translate-y-0.5 transition-all duration-300" data-testid="blog-cta-primary">
                    {cta.label} <ArrowRight size={18} />
                  </Link>
                </div>
              </div>

              <RelatedPosts post={post} />

              {/* Comments */}
              <section className="mt-16" data-testid="blog-comments">
                <h2 className="font-heading text-2xl font-bold text-[#0F172A] flex items-center gap-2.5">
                  <MessageCircle size={22} className="text-[#0EA5E9]" />
                  Comments <span className="text-[#94A3B8] font-semibold">({comments.length})</span>
                </h2>

                <form onSubmit={submitComment} className="mt-6 bg-white rounded-[1.5rem] p-6 border border-slate-100 shadow-sm space-y-4" data-testid="comment-form">
                  <input
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder="Your name"
                    maxLength={80}
                    required
                    className="w-full bg-[#F8FAFC] border border-slate-200 rounded-2xl px-5 py-3 focus:outline-none focus:border-[#0EA5E9] transition-all"
                    data-testid="comment-name"
                  />
                  <textarea
                    value={form.text}
                    onChange={(e) => setForm((f) => ({ ...f, text: e.target.value }))}
                    placeholder="Share your thoughts..."
                    rows={3}
                    maxLength={1000}
                    required
                    className="w-full bg-[#F8FAFC] border border-slate-200 rounded-2xl px-5 py-3 resize-none focus:outline-none focus:border-[#0EA5E9] transition-all"
                    data-testid="comment-text"
                  />
                  <button
                    type="submit"
                    disabled={submitting}
                    className="inline-flex items-center gap-2 bg-[#0A2540] text-white font-bold px-6 py-3 rounded-full hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-60"
                    data-testid="comment-submit"
                  >
                    {submitting ? <Loader2 size={17} className="animate-spin" /> : <Send size={16} />}
                    {submitting ? "Posting..." : "Post comment"}
                  </button>
                  <p className="text-xs text-[#94A3B8]">Comments are reviewed by our team before they appear.</p>
                </form>

                <div className="mt-6 space-y-4" data-testid="comment-list">
                  {comments.length === 0 ? (
                    <p className="text-[#64748B]" data-testid="comment-empty">Be the first to comment on this post.</p>
                  ) : (
                    comments.map((c) => (
                      <div key={c.id} className="bg-white rounded-2xl p-5 border border-slate-100" data-testid="comment-item">
                        <div className="flex items-center gap-3">
                          <span className="grid place-items-center w-9 h-9 rounded-full bg-[#0EA5E9]/12 text-[#0EA5E9] font-heading font-bold text-sm">
                            {c.name.trim().charAt(0).toUpperCase()}
                          </span>
                          <div>
                            <p className="font-semibold text-[#0F172A] leading-none">{c.name}</p>
                            <p className="mt-1 text-xs text-[#94A3B8]">{fmtDate(c.created_at)}</p>
                          </div>
                        </div>
                        <p className="mt-3 text-[#334155] leading-relaxed whitespace-pre-line">{c.text}</p>
                      </div>
                    ))
                  )}
                </div>
              </section>
            </div>
          </section>
        </article>

        <FinalCTA />
      </main>
      <Footer />
    </div>
  );
}
